/* eslint-disable max-lines -- Pre-existing violation */
import {
  Attributes,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  DataStatus,
  Dialog,
  DialogFooter,
  Flag,
  Form,
  FormButton,
  FormField,
  Table,
  type TableCellBaseProps,
  TableRow,
  Tooltip,
  type FormSubmitHandler,
  type FormControl,
} from 'platform/components';
import {Box, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import styled, {css} from 'styled-components';
import {match, Pattern} from 'ts-pattern';
import {z} from 'zod';

import {KeyboardEvent, ReactNode, useRef, useState} from 'react';
import {useWatch} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {
  newVehicleApi,
  useBulkUpdateVariantMatrixMutation,
  useGetVariantMatrixQuery,
  usePatchVariantMatrixCellMutation,
  usePutVariantMatrixMutation,
  type EngineResponseBody,
  type VariantResponseBody,
} from '@dms/api/new-vehicle';
import {
  MoneyText,
  getNewVehicleEnumLabel,
  useNewVehicleCatalogueLabels,
} from '@dms/features/new-vehicle';

import {isNotNilOrEmpty, type ApiError} from 'shared/web';

import {tabLabels} from '../constants/tabLabels';
import {useCatalogueVatRate} from '../hooks/useCatalogueVatRate';
import {useGroupedNewTrims} from '../hooks/useGroupedNewTrims';
import {usePricedItemGroup} from '../hooks/usePricedItemGroup';
import {usePublishedPriceSave} from '../hooks/usePublishedPriceSave';
import {variantCellSchema, type VariantCellValues} from '../schemas/variantCellSchema';
import {
  toVariantTechnicalData,
  toVariantTechnicalDataValues,
} from '../schemas/variantTechnicalDataSchema';
import {ITEM_CHANGE_CANCELLED} from '../types/PricedItemChangeOutcome';
import {Cell, column, handleCatalogueError, isStaleCatalogueVersion, t} from '../utils/catalogueUi';
import {createdItem} from '../utils/createdItem';
import {formatConsumptionAndEmission} from '../utils/formatConsumptionAndEmission';
import {isInPricelist} from '../utils/isInPricelist';
import {moneyInput, negativeMargin, priceInput} from '../utils/moneyInput';
import {pricedItemGroupKeys} from '../utils/pricedItemGroupKeys';
import {statusWithPriceError} from '../utils/statusWithPriceError';
import {unchangedStoredVersion} from '../utils/unchangedStoredVersion';
import {variantChangeName} from '../utils/variantChangeName';
import {variantEnabledChange} from '../utils/variantEnabledChange';
import {GrossPriceField} from './GrossPriceField';
import {NegativeMarginWarning} from './NegativeMarginWarning';
import {NewPricelistVersionDialog} from './NewPricelistVersionDialog';
import {NumberField} from './NumberField';
import {PriceAdjustmentDialog} from './PriceAdjustmentDialog';
import {PricelistVersionHistoryDialog} from './PricelistVersionHistoryDialog';
import {PriceWithGross} from './PriceWithGross';
import {VariantTechnicalDataFields} from './VariantTechnicalDataFields';

const bulkSchema = z.object({
  kind: z.enum(['percentage', 'amount', 'set', 'fillDown']),
  direction: z.enum(['up', 'down']),
  value: moneyInput,
});
type EditValues = VariantCellValues;
type EditingCell = {
  trimId: string;
  engine: EngineResponseBody;
  variant?: VariantResponseBody;
};
type BulkValues = z.infer<typeof bulkSchema>;

/**
 * One trim × engine position. Reads as a tile so a sparse matrix shows at a glance which
 * combinations are offered; the whole tile opens the editor, the checkbox only selects.
 */
const MatrixTile = styled.div<{
  $isSelected: boolean;
  $isPlaceholder: boolean;
  $isInteractive: boolean;
}>(
  ({theme, $isSelected, $isPlaceholder, $isInteractive}) => css`
    box-sizing: border-box;
    height: 100%;
    padding: ${theme.getSize(2)};
    ${
      $isPlaceholder &&
      css`
        display: flex;
        align-items: center;
      `
    }
    border-radius: ${theme.radii.small};
    border: 1px ${$isPlaceholder ? 'dashed' : 'solid'}
      ${$isSelected ? theme.colors.general.accent : theme.colors.general.separator};
    background-color: ${
      $isSelected ? theme.colors.palettes.blue[10][100] : theme.colors.general.white
    };
    cursor: ${$isInteractive ? 'pointer' : 'default'};

    &:hover {
      border-color: ${
        $isInteractive // eslint-disable-line no-nested-ternary -- Pre-existing violation
          ? theme.colors.general.accent
          : $isSelected
            ? theme.colors.general.accent
            : theme.colors.general.separator
      };
    }

    &:focus-visible {
      outline: ${theme.outline.default} ${theme.colors.general.accent};
      outline-offset: ${theme.outline.offset};
    }
  `
);

/** Engine cell. TableRow reads the aria props and the height opt-in from here. */
function MatrixCell(
  props: Pick<TableCellBaseProps, 'role' | 'ariaColIndex' | 'ariaSelected' | 'isCellFullHeight'> & {
    children: ReactNode;
  }
) {
  return (
    <Box padding={2} height="100%">
      <VStack height="100%" justify="center">
        {props.children}
      </VStack>
    </Box>
  );
}

const toEditValues = (variant: VariantResponseBody | undefined): EditValues => ({
  priceNet: variant?.priceNet ?? '0.00',
  costNet: variant?.costNet ?? '',
  orderCode: variant?.orderCode ?? '',
  enabled: variant?.enabled ?? true,
  gvwKg: variant?.gvwKg?.toString() ?? '',
  payloadKg: variant?.payloadKg?.toString() ?? '',
  technicalData: toVariantTechnicalDataValues(variant?.technicalData),
});

/** Omits technicalData: the server keeps a cell's stored data and fuel-checks only data a write sends. */
const toVariantWriteRequest = (variant: VariantResponseBody) => ({
  trimId: variant.trimId,
  engineId: variant.engineId,
  priceNet: variant.priceNet,
  costNet: variant.costNet,
  orderCode: variant.orderCode,
  enabled: variant.enabled,
  roofHeight: variant.roofHeight,
  loadLength: variant.loadLength,
  gvwKg: variant.gvwKg,
  payloadKg: variant.payloadKg,
  reviewFlags: variant.reviewFlags,
  version: variant.version,
});

const toCellWriteRequest = (
  cell: EditingCell,
  values: EditValues,
  canViewCost: boolean,
  options?: {isPriceHeldBack: boolean}
) => ({
  trimId: cell.trimId,
  engineId: cell.engine.id,
  priceNet: values.priceNet,
  orderCode: values.orderCode,
  enabled: values.enabled,
  costNet: canViewCost ? values.costNet || null : (cell.variant?.costNet ?? null),
  roofHeight: cell.variant?.roofHeight ?? null,
  loadLength: cell.variant?.loadLength ?? null,
  gvwKg: values.gvwKg === '' ? null : Number(values.gvwKg),
  payloadKg: values.payloadKg === '' ? null : Number(values.payloadKg),
  technicalData: toVariantTechnicalData(values.technicalData, cell.engine),
  // Saving the dialog reviews its fields (OD-R13); the server keeps cost_unreviewed without cost access.
  // A published price waiting for its pricelist version is not reviewed yet, so neither is the rest.
  reviewFlags: options?.isPriceHeldBack ? (cell.variant?.reviewFlags ?? []) : [],
  version: cell.variant?.version ?? 1,
});

/** A save with a changed price leaves the price flagged for review, so it cannot promise a review. */
function EditVariantSubmit(props: {
  control: FormControl<EditValues>;
  storedPriceNet: string | undefined;
  hasClearableReviewFlags: boolean;
}) {
  const priceNet = useWatch({control: props.control, name: 'priceNet'});
  const isPriceChanged = priceInput.safeParse(priceNet).data !== props.storedPriceNet;
  return (
    <FormButton
      control={props.control}
      type="submit"
      title={
        props.hasClearableReviewFlags && !isPriceChanged
          ? t('entity.newVehicle.catalogue.markAsReviewed', 'Mark as reviewed')
          : t('general.actions.save', 'Save')
      }
    />
  );
}

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function VariantMatrix({
  modelYearId,
  currency,
  canWrite,
  canViewCost,
  modelYearVersion,
  pricelistVersionLabel,
}: {
  modelYearId: string;
  currency: string;
  canWrite: boolean;
  canViewCost: boolean;
  modelYearVersion: number;
  pricelistVersionLabel: string;
}) {
  const query = useGetVariantMatrixQuery({modelYearId}, {refetchOnMountOrArgChange: true});
  const {getTransmission} = useNewVehicleCatalogueLabels();
  const [patchCell] = usePatchVariantMatrixCellMutation();
  const [putMatrix] = usePutVariantMatrixMutation();
  const [readMatrix] = newVehicleApi.useLazyGetVariantMatrixQuery();
  const [getVariant] = newVehicleApi.useLazyGetVariantQuery();
  const [bulkUpdate] = useBulkUpdateVariantMatrixMutation();
  const publishedPrice = usePublishedPriceSave(modelYearId);
  const vatRate = useCatalogueVatRate(modelYearId);
  const [editing, setEditing] = useState<EditingCell>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const cellRefs = useRef(new Map<number, HTMLDivElement>());
  const groupedNewTrims = useGroupedNewTrims(modelYearId);
  // An adjustment applies at once and moves the label, while the open group lives only in the
  // browser and is not confirmed yet (Q3): no adjustment while a group of the year is open.
  const isGroupOpen = isNotNil(usePricedItemGroup(modelYearId));
  const matrix = query.data;
  const cells = matrix?.cells ?? [];
  const storedTrims = matrix?.trims ?? [];
  /**
   * The rows: the stored trims, then the trims that the open group creates (F1 addendum 8.4). A row
   * of a grouped new trim takes new cells only, as it has no Variant: a new cell joins the group and
   * names the trim by its client id, so the batch sends it after the trim create.
   */
  const trims = [
    ...storedTrims.map(({id, name, publicationStatus}) => ({
      id,
      name,
      publicationStatus,
      isGroupedNew: false,
    })),
    ...groupedNewTrims
      .filter((created) => storedTrims.every(({id}) => id !== created.id))
      .map((created) => ({...created, isGroupedNew: true})),
  ];
  const engines = matrix?.engines ?? [];
  const hasAxes = trims.length > 0 && engines.length > 0;
  /**
   * A Variant of a Draft trim or a disabled Variant is outside the price list (by its stored state,
   * as on the server): its price and its create are written without a pricelist version.
   */
  const trimStatus = (trimId: string) => trims.find(({id}) => id === trimId)?.publicationStatus;
  const isOutsidePriceList = (variant: {trimId: string; enabled: boolean}) =>
    !isInPricelist.variant(variant.enabled, trimStatus(variant.trimId));
  /** Focus follows the rendered grid, not the sparse cell list, so gaps stay reachable. */
  const moveFocus = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    if (!engines.length) {
      return;
    }
    const row = Math.floor(index / engines.length);
    const columnIndex = index % engines.length;
    const clamp = (value: number, max: number) => Math.max(0, Math.min(max, value));
    const next =
      event.key === 'ArrowRight' // eslint-disable-line no-nested-ternary -- Pre-existing violation
        ? row * engines.length + clamp(columnIndex + 1, engines.length - 1)
        : event.key === 'ArrowLeft' // eslint-disable-line no-nested-ternary -- Pre-existing violation
          ? row * engines.length + clamp(columnIndex - 1, engines.length - 1)
          : event.key === 'ArrowDown' // eslint-disable-line no-nested-ternary -- Pre-existing violation
            ? clamp(row + 1, trims.length - 1) * engines.length + columnIndex
            : event.key === 'ArrowUp'
              ? clamp(row - 1, trims.length - 1) * engines.length + columnIndex
              : index;
    if (next === index) {
      return;
    }
    event.preventDefault();
    setFocusedIndex(next);
    cellRefs.current.get(next)?.focus();
  };
  const toggleSelection = (variantId: string) =>
    setSelectedIds((ids) =>
      ids.includes(variantId) ? ids.filter((id) => id !== variantId) : [...ids, variantId]
    );
  /** Space selects for bulk pricing, Enter edits or creates the variant, arrows walk the grid. */
  const handleCellKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    position: {index: number; cell?: VariantResponseBody; edit: () => void}
  ) => {
    if (event.key === ' ' && position.cell) {
      event.preventDefault();
      toggleSelection(position.cell.id);
      return;
    }
    if (event.key === 'Enter' && canWrite) {
      event.preventDefault();
      position.edit();
      return;
    }
    if (event.key === 'Escape') {
      setEditing(undefined);
      return;
    }
    moveFocus(event, position.index);
  };
  /**
   * An enable switch of a Variant of an online trim puts its price into the dealer price list or
   * takes it out (P1): a published year asks for a new pricelist version first. An enable sends the
   * price of the form; a disable with a changed price is refused by the server (P2), so nothing is sent.
   */
  const switchEnabled = async (
    cell: EditingCell,
    variant: VariantResponseBody,
    values: EditValues,
    setErrors: Parameters<FormSubmitHandler<EditValues>>[1]
  ) => {
    if (
      !values.enabled &&
      Number(values.priceNet) !== Number(variant.priceNet) &&
      (await publishedPrice.readIsPublished())
    ) {
      setErrors([statusWithPriceError('priceNet')]);
      return;
    }
    const items = [
      variantEnabledChange({
        variant: values.enabled
          ? {...variant, orderCode: values.orderCode, priceNet: values.priceNet}
          : variant,
        trimName: trims.find(({id}) => id === cell.trimId)?.name ?? '',
        engineName: cell.engine.name,
        enabled: values.enabled,
      }),
    ];
    const outcome = await publishedPrice.writePricedItemChange({
      items,
      entry: {
        key: pricedItemGroupKeys.variant(variant.id),
        title: items[0].name,
        // Two Variant writes of one batch cannot use one order code (`duplicateValue`).
        usedValues: pricedItemGroupKeys.usedValue('variant', 'code', values.orderCode),
        // The body is the editor's: a write to the Variant after it was added fails the entry.
        build: () =>
          unchangedStoredVersion(variant.version, () =>
            getVariant({variantId: variant.id}, false)
              .unwrap()
              .then((stored) => stored.version)
          ).then((version) => ({
            items,
            operations: [
              {
                type: 'variantMatrix.patchCell',
                targetId: variant.id,
                body: {...toCellWriteRequest(cell, values, canViewCost), version},
              },
            ],
          })),
      },
      write: (pricelistVersion) =>
        patchCell({
          modelYearId,
          variantId: variant.id,
          variantItemWriteRequestBody: {
            ...toCellWriteRequest(cell, values, canViewCost),
            ...(pricelistVersion && {pricelistVersion}),
          },
        }).unwrap(),
    });
    // A cancelled pricelist version keeps the cell editor open with its values.
    if (outcome !== ITEM_CHANGE_CANCELLED) {
      setEditing(undefined);
    }
  };
  const saveCell: FormSubmitHandler<EditValues> = async (values, setErrors) => {
    if (!editing) {
      return;
    }
    const {variant} = editing;
    // A Variant of a Draft trim stays outside the price list whether it is enabled or not.
    const isEnabledSwitch =
      variant?.enabled !== values.enabled && isInPricelist.trim(trimStatus(editing.trimId));
    const request: Promise<boolean | void> = match({variant, isEnabledSwitch})
      .with({variant: Pattern.nonNullable, isEnabledSwitch: true}, ({variant: stored}) =>
        switchEnabled(editing, stored, values, setErrors)
      )
      .with({variant: Pattern.nonNullable}, ({variant: stored}) =>
        publishedPrice.save({
          values,
          storedValues: toEditValues(stored),
          priceField: 'priceNet',
          scope: 'variant',
          resourceId: stored.id,
          isDraftItem: isOutsidePriceList(stored),
          save: (cellValues, options) =>
            patchCell({
              modelYearId,
              variantId: stored.id,
              variantItemWriteRequestBody: toCellWriteRequest(
                editing,
                cellValues,
                canViewCost,
                options
              ),
            }).unwrap(),
          onDone: () => setEditing(undefined),
        })
      )
      .otherwise(() => saveNewCell(editing, values));
    await request.catch(async (error: ApiError) => {
      handleCatalogueError(error, setErrors);
      if (isStaleCatalogueVersion(error)) {
        await query.refetch();
        setEditing(undefined);
      }
    });
  };
  /**
   * A new cell is one matrix PUT of the stored cells and the new one. In an open group the cells of
   * the year merge into one entry; its build reads the matrix again at "Review and confirm".
   */
  const saveNewCell = (editing: EditingCell, values: EditValues) => {
    const items = isOutsidePriceList({trimId: editing.trimId, enabled: values.enabled})
      ? []
      : [
          createdItem(
            'variant',
            `${editing.trimId}:${editing.engine.id}`,
            variantChangeName(
              trims.find(({id}) => id === editing.trimId)?.name ?? '',
              editing.engine.name,
              values.orderCode
            ),
            values.priceNet
          ),
        ];
    const newCell = toCellWriteRequest(editing, values, canViewCost);
    return publishedPrice
      .writePricedItemChange({
        items,
        namesGroupedItem: trims.some(({id, isGroupedNew}) => id === editing.trimId && isGroupedNew),
        entry: {
          key: pricedItemGroupKeys.matrixPut(modelYearId),
          title: tabLabels['variant-matrix'],
          matrixCells: [newCell],
          matrixTrimIds: [editing.trimId],
          build: () =>
            readMatrix({modelYearId}, false)
              .unwrap()
              .then((current) => ({
                items,
                operations: [
                  {
                    type: 'variantMatrix.put',
                    body: {
                      cells: [...current.cells.map(toVariantWriteRequest), newCell],
                      version: current.version,
                    },
                  },
                ],
              })),
        },
        // The stored cells go unchanged. They and the matrix version are read from the cache when
        // the write is sent, as another write can land while the pricelist version dialog is open.
        write: (pricelistVersion) =>
          readMatrix({modelYearId}, true)
            .unwrap()
            .then((current) =>
              putMatrix({
                modelYearId,
                variantMatrixWriteRequestBody: {
                  cells: [...current.cells.map(toVariantWriteRequest), newCell],
                  version: current.version,
                  ...(pricelistVersion && {pricelistVersion}),
                },
              }).unwrap()
            ),
      })
      .then((outcome) => {
        // A cancelled pricelist version keeps the cell editor open with its values.
        if (outcome !== ITEM_CHANGE_CANCELLED) {
          setEditing(undefined);
        }
      });
  };
  // The server keeps cost_unreviewed for a user without cost access, so only promise what a save clears.
  const hasClearableReviewFlags = Boolean(
    editing?.variant?.reviewFlags.some((flag) => canViewCost || flag !== 'cost_unreviewed')
  );
  const bulk = async (values: BulkValues) => {
    const value =
      (values.kind === 'percentage' || values.kind === 'amount') &&
      values.direction === 'down' &&
      values.value !== '0.00'
        ? `-${values.value}`
        : values.value;
    const bulkFields = (fields: ('priceNet' | 'costNet')[], clearReviewFlags = true) =>
      bulkUpdate({
        modelYearId,
        bulkVariantUpdateRequestBody: {
          kind: values.kind,
          value,
          scope: {variantIds: selectedIds},
          fields,
          clearReviewFlags,
          version: matrix?.version ?? modelYearVersion,
        },
      }).unwrap();
    // Every selected Variant outside the price list: its prices go through bulk, as on a draft year.
    // A selection that also holds a Variant of the price list keeps the adjustment.
    const isDraftSelection =
      selectedIds.length > 0 &&
      selectedIds.every((variantId) => {
        const cell = cells.find(({id}) => id === variantId);
        return isNotNil(cell) && isOutsidePriceList(cell);
      });
    // A published year takes the price only as an adjustment; the cost still goes through bulk.
    const adjustOrBulk = async () => {
      if (
        values.kind !== 'fillDown' &&
        (await publishedPrice.confirmAdjustment(
          {kind: values.kind, value, scope: 'variant', resourceIds: selectedIds},
          {
            // Closed once the cost is saved, so the same relative change cannot be applied twice.
            // Like the editors, it reviews nothing while the price waits for its version.
            saveFirst: canViewCost
              ? {
                  saved: 'costs',
                  run: () => bulkFields(['costNet'], false).then(() => setBulkOpen(false)),
                }
              : undefined,
            isDraftItem: isDraftSelection,
            onDone: () => setBulkOpen(false),
          }
        ))
      ) {
        return;
      }
      await bulkFields(canViewCost ? ['priceNet', 'costNet'] : ['priceNet']).catch(
        publishedPrice.rejectDirectWrite
      );
      setBulkOpen(false);
    };
    await adjustOrBulk().catch(handleCatalogueError);
  };
  return (
    <VStack spacing={4}>
      <HStack justify="space-between" align="center">
        <HStack spacing={2}>
          <Show when={canWrite}>
            <Tooltip
              label={t(
                'entity.newVehicle.catalogue.selectCellsFirst',
                'Select cells with Space first'
              )}
              isDisabled={selectedIds.length > 0}
            >
              <Button
                variant={selectedIds.length ? 'primary' : 'secondary'}
                title={t('entity.newVehicle.catalogue.bulkUpdate', 'Bulk update')}
                suffix={selectedIds.length ? `(${selectedIds.length})` : undefined}
                isDisabled={!selectedIds.length}
                onClick={() => setBulkOpen(true)}
              />
            </Tooltip>
          </Show>
          <Show when={canWrite}>
            <Tooltip
              label={t(
                'entity.newVehicle.catalogue.itemGroup.priceChangeOfExistingRow',
                'A price change cannot be made while a group is open. Confirm or discard the group first.'
              )}
              isDisabled={!isGroupOpen}
            >
              <Button
                variant="secondary"
                title={t(
                  'entity.newVehicle.catalogue.midYearAdjustment',
                  'Mid-year price adjustment'
                )}
                isDisabled={isGroupOpen}
                onClick={() => setAdjustOpen(true)}
              />
            </Tooltip>
          </Show>
          <Button
            variant="secondary"
            title={t('entity.newVehicle.catalogue.pricelistHistory', 'Pricelist version history')}
            onClick={() => setHistoryOpen(true)}
          />
        </HStack>
        <HStack spacing={2} align="center">
          <Text size="small" color="secondary">
            {selectedIds.length
              ? `${selectedIds.length} ${t('entity.newVehicle.catalogue.selected', 'selected')}`
              : t('general.labels.selectedNothing', 'Nothing selected')}
          </Text>
          <Show when={canWrite && cells.length > 0}>
            <Button
              variant="link"
              title={
                selectedIds.length
                  ? t('general.actions.clear', 'Clear')
                  : t('general.actions.selectAll', 'Select all')
              }
              onClick={() =>
                setSelectedIds(selectedIds.length ? [] : cells.map((variant) => variant.id))
              }
            />
          </Show>
          <Button
            variant="link"
            title={t('entity.newVehicle.catalogue.keyboardShortcuts', 'Keyboard shortcuts')}
            onClick={() => setShortcutsOpen(true)}
          />
        </HStack>
      </HStack>
      <Card
        title={t('entity.newVehicle.catalogue.variantMatrix', 'Variant matrix')}
        subtitle={t(
          'entity.newVehicle.catalogue.variantMatrixHint',
          'Rows are trim levels, columns are engines. Each cell is one orderable variant with its own order code and price.'
        )}
      >
        <DataStatus
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={!hasAxes}
          emptyMessage={t(
            'entity.newVehicle.catalogue.variantMatrixNeedsAxes',
            'Add trim levels and engines first, then price every combination here.'
          )}
        >
          <Table
            role="grid"
            // Fixed layout splits what the trim column leaves equally, whatever the header text.
            tableLayout="fixed"
            ariaRowCount={trims.length + 1}
            ariaColCount={engines.length + 1}
            columns={[
              column(t('entity.newVehicle.catalogue.trimEngine', 'Trim / engine'), {width: '18%'}),
              ...engines.map((engine) =>
                column(
                  <VStack spacing={0}>
                    <Text size="small" alternative>
                      {engine.name}
                    </Text>
                    <Text size="xSmall" color="secondary">
                      {engine.powerKw} {t('entity.newVehicle.labels.kw', 'kW')} ·{' '}
                      {getTransmission(engine.transmission)}
                    </Text>
                  </VStack>
                )
              ),
            ]}
          >
            {trims.map((trim, rowIndex) => (
              <TableRow key={trim.id} role="row" ariaRowIndex={rowIndex + 2}>
                <Cell role="gridcell" ariaColIndex={1}>
                  <HStack spacing={2} align="center" wrap>
                    {trim.name}
                    <Show when={trim.isGroupedNew}>
                      <Tooltip
                        label={t(
                          'entity.newVehicle.catalogue.itemGroup.barHint',
                          'Nothing is saved until you confirm. The dealer price list and its label do not change.'
                        )}
                      >
                        <Flag label={t('general.labels.new', 'New')} colorScheme="blue" isSubtle />
                      </Tooltip>
                    </Show>
                  </HStack>
                </Cell>
                {engines.map((engine, columnIndex) => {
                  const cell = cells.find(
                    (item) => item.trimId === trim.id && item.engineId === engine.id
                  );
                  const index = rowIndex * engines.length + columnIndex;
                  const isSelected = Boolean(cell && selectedIds.includes(cell.id));
                  const hasNegativeMargin =
                    canViewCost && negativeMargin(cell?.priceNet, cell?.costNet) !== undefined;
                  const consumptionAndEmission = formatConsumptionAndEmission(cell?.technicalData);
                  const isCellWritable = canWrite && (!trim.isGroupedNew || isNil(cell));
                  const openEditor = () => {
                    if (isCellWritable) {
                      setEditing({trimId: trim.id, engine, variant: cell});
                    }
                  };
                  return (
                    <MatrixCell
                      key={engine.id}
                      isCellFullHeight
                      role="gridcell"
                      ariaColIndex={columnIndex + 2}
                      ariaSelected={isSelected}
                    >
                      {isCellWritable || cell ? (
                        <MatrixTile
                          ref={(element) => {
                            if (element) {
                              cellRefs.current.set(index, element);
                            } else {
                              cellRefs.current.delete(index);
                            }
                          }}
                          $isSelected={isSelected}
                          $isPlaceholder={!cell}
                          $isInteractive={isCellWritable}
                          data-testid={`matrix-cell-${index}`}
                          role={isCellWritable ? 'button' : undefined}
                          aria-label={`${trim.name}, ${engine.name}`}
                          tabIndex={index === focusedIndex ? 0 : -1}
                          onFocus={() => setFocusedIndex(index)}
                          onClick={openEditor}
                          onKeyDown={(event) =>
                            handleCellKeyDown(event, {index, cell, edit: openEditor})
                          }
                        >
                          {cell ? (
                            <VStack spacing={1}>
                              <HStack spacing={2} align="center" justify="space-between">
                                <Text size="xSmall" color="secondary" noWrap>
                                  {cell.orderCode}
                                </Text>
                                <Show when={canWrite}>
                                  {/* Selection is for bulk pricing only, so it must not open the editor. */}
                                  <div onClick={(event) => event.stopPropagation()}>
                                    <Checkbox
                                      value={isSelected}
                                      aria-label={`${trim.name}, ${engine.name}`}
                                      onChange={() => toggleSelection(cell.id)}
                                    />
                                  </div>
                                </Show>
                              </HStack>
                              <PriceWithGross
                                amount={cell.priceNet}
                                currency={currency}
                                vatRate={vatRate}
                                align="left"
                                alternative
                              />
                              <Show when={isNotNilOrEmpty(consumptionAndEmission)}>
                                <Text size="xSmall" color="secondary">
                                  {consumptionAndEmission}
                                </Text>
                              </Show>
                              <Show when={canViewCost}>
                                <HStack spacing={1}>
                                  <Text size="xSmall" color="secondary">
                                    {t('entity.newVehicle.catalogue.cost', 'Cost')}
                                  </Text>
                                  <MoneyText
                                    amount={cell.costNet ?? '0.00'}
                                    currency={currency}
                                    size="xSmall"
                                  />
                                </HStack>
                              </Show>
                              <Show
                                when={
                                  !cell.enabled || cell.reviewFlags.length > 0 || hasNegativeMargin
                                }
                              >
                                <HStack spacing={1} wrap>
                                  <Show when={!cell.enabled}>
                                    <Flag
                                      label={t('entity.newVehicle.catalogue.disabled', 'Disabled')}
                                      colorScheme="neutral"
                                      isSubtle
                                    />
                                  </Show>
                                  <Show when={cell.reviewFlags.length > 0}>
                                    <Flag
                                      label={t(
                                        'entity.newVehicle.catalogue.needsReview',
                                        'Needs review'
                                      )}
                                      colorScheme="orange"
                                      isSubtle
                                    />
                                  </Show>
                                  <Show when={hasNegativeMargin}>
                                    <Flag
                                      label={t(
                                        'entity.newVehicle.catalogue.negativeMarginFlag',
                                        'Negative margin'
                                      )}
                                      colorScheme="red"
                                      isSubtle
                                    />
                                  </Show>
                                </HStack>
                              </Show>
                            </VStack>
                          ) : (
                            <HStack spacing={1} align="center">
                              <Icon value="content/add" size={4} color="text.secondary" />
                              <Text size="xSmall" color="secondary" noWrap>
                                {t('entity.newVehicle.catalogue.addVariant', 'Add variant')}
                              </Text>
                            </HStack>
                          )}
                        </MatrixTile>
                      ) : (
                        <Text size="xSmall" color="tertiary">
                          {t('entity.newVehicle.catalogue.notOffered', 'Not offered')}
                        </Text>
                      )}
                    </MatrixCell>
                  );
                })}
              </TableRow>
            ))}
          </Table>
        </DataStatus>
      </Card>
      <Dialog
        isOpen={Boolean(editing)}
        onClose={() => setEditing(undefined)}
        title={t('entity.newVehicle.catalogue.editVariant', 'Edit variant')}
        withAdditionalFooter
      >
        {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
        {editing ? (
          <Form<EditValues>
            experimentalZodSchema={variantCellSchema}
            defaultValues={toEditValues(editing.variant)}
            onSubmit={saveCell}
          >
            {(control) => (
              <VStack spacing={2}>
                <FormField
                  control={control}
                  name="enabled"
                  type="checkbox"
                  label={t('entity.newVehicle.catalogue.enabled', 'Enabled')}
                />
                <GrossPriceField
                  control={control}
                  name="priceNet"
                  vatRate={vatRate}
                  label={t('entity.newVehicle.catalogue.priceNet', 'Price net')}
                />
                <Show when={canViewCost}>
                  <NumberField
                    control={control}
                    name="costNet"
                    label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
                  />
                </Show>
                <NegativeMarginWarning
                  control={control}
                  priceName="priceNet"
                  costName="costNet"
                  currency={currency}
                  canViewCost={canViewCost}
                />
                <FormField
                  control={control}
                  name="orderCode"
                  type="text"
                  label={t('entity.newVehicle.catalogue.orderCode', 'Order code')}
                  isRequired
                />
                <VariantTechnicalDataFields control={control} engine={editing.engine} />
                <DialogFooter>
                  <ButtonGroup align="right">
                    <Button
                      variant="secondary"
                      title={t('general.actions.discard', 'Discard')}
                      onClick={() => setEditing(undefined)}
                    />
                    <EditVariantSubmit
                      control={control}
                      storedPriceNet={editing.variant?.priceNet}
                      hasClearableReviewFlags={hasClearableReviewFlags}
                    />
                  </ButtonGroup>
                </DialogFooter>
              </VStack>
            )}
          </Form>
        ) : null}
      </Dialog>
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
      <Dialog
        isOpen={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title={t('entity.newVehicle.catalogue.bulkUpdate', 'Bulk update')}
        size="small"
        withAdditionalFooter
      >
        <Form<BulkValues>
          experimentalZodSchema={bulkSchema}
          defaultValues={{kind: 'amount', direction: 'up', value: '0.00'}}
          onSubmit={bulk}
        >
          {(control) => (
            <VStack spacing={2}>
              <FormField
                control={control}
                name="kind"
                type="choice"
                menuInPortal
                label={t('entity.newVehicle.catalogue.changeKind', 'Change kind')}
                options={[
                  {
                    label: getNewVehicleEnumLabel('changeKind', 'percentage', t),
                    value: 'percentage',
                  },
                  {label: getNewVehicleEnumLabel('changeKind', 'amount', t), value: 'amount'},
                  {label: getNewVehicleEnumLabel('changeKind', 'set', t), value: 'set'},
                  // One adjustment cannot copy a price down a column.
                  ...(publishedPrice.isPublished
                    ? []
                    : [
                        {
                          label: getNewVehicleEnumLabel('changeKind', 'fillDown', t),
                          value: 'fillDown',
                        },
                      ]),
                ]}
              />
              <FormField
                control={control}
                name="direction"
                type="choice"
                menuInPortal
                label={t('entity.newVehicle.catalogue.direction', 'Direction')}
                options={[
                  {label: getNewVehicleEnumLabel('direction', 'up', t), value: 'up'},
                  {label: getNewVehicleEnumLabel('direction', 'down', t), value: 'down'},
                ]}
              />
              <NumberField
                control={control}
                name="value"
                label={t('general.labels.amount', 'Amount')}
              />
              <DialogFooter>
                <ButtonGroup align="right">
                  <Button
                    variant="secondary"
                    title={t('general.actions.discard', 'Discard')}
                    onClick={() => setBulkOpen(false)}
                  />
                  <FormButton
                    control={control}
                    type="submit"
                    title={t('general.actions.apply', 'Apply')}
                  />
                </ButtonGroup>
              </DialogFooter>
            </VStack>
          )}
        </Form>
      </Dialog>
      <PriceAdjustmentDialog
        isOpen={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        modelYearId={modelYearId}
        modelYearVersion={modelYearVersion}
        pricelistVersionLabel={pricelistVersionLabel}
        currency={currency}
        canSchedule={publishedPrice.isPublished}
      />
      <PricelistVersionHistoryDialog
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        modelYearId={modelYearId}
        modelYearVersion={modelYearVersion}
        currency={currency}
        canWrite={canWrite}
      />
      <Dialog
        isOpen={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
        title={t('entity.newVehicle.catalogue.keyboardShortcuts', 'Keyboard shortcuts')}
      >
        <Attributes
          rows={[
            {
              label: t('entity.newVehicle.catalogue.arrowKeys', 'Arrow keys'),
              value: t('entity.newVehicle.catalogue.moveBetweenCells', 'Move between cells'),
            },
            {
              label: t('entity.newVehicle.catalogue.spaceKey', 'Space'),
              value: t('entity.newVehicle.catalogue.selectDeselect', 'Select/deselect'),
            },
            {
              label: t('entity.newVehicle.catalogue.enterKey', 'Enter'),
              value: t('entity.newVehicle.catalogue.editPrice', 'Edit price'),
            },
            {
              label: t('entity.newVehicle.catalogue.escapeKey', 'Escape'),
              value: t('entity.newVehicle.catalogue.close', 'Close'),
            },
          ]}
        />
      </Dialog>
    </VStack>
  );
}
